import { useEffect, type MutableRefObject } from 'react';
import sendUploadAnalytics from '@/utils/analytics/sendUploadAnalytics';
import { InputType } from '@/types';

import type { IImageInput, IVideoInput } from '@/types';

type UpdateInput = (args: {
  id: string;
  value: Partial<IImageInput | IVideoInput>;
}) => void;

interface UsePendingUploadsOptions {
  isAuthorized: boolean | undefined;
  pendingUploads: { id: string; file: File }[];
  clearPendingUploads: () => void;
  uploadMedia: (file: File, id: string) => Promise<string | null>;
  updateInput: UpdateInput;
  openDrivePickerForInput: (id: string, inputType: InputType) => void;
  pendingDriveAction: MutableRefObject<{
    id: string;
    inputType: InputType;
  } | null>;
}

const usePendingUploads = ({
  isAuthorized,
  pendingUploads,
  clearPendingUploads,
  uploadMedia,
  updateInput,
  openDrivePickerForInput,
  pendingDriveAction,
}: UsePendingUploadsOptions) => {
  // Handle pending uploads after auth. No token polling here: `uploadMedia` →
  // `uploadToCDN` refreshes the access token itself when it's missing (e.g. the
  // auth flag flips just before the token propagates), then uploads. Each
  // `uploadMedia` always resolves and clears `isLoading`, so no timers/timeout.
  useEffect(() => {
    if (!isAuthorized || !pendingUploads.length) return;

    // Capture and clear immediately so this doesn't re-trigger per upload.
    const uploads = [...pendingUploads];
    clearPendingUploads();

    uploads.forEach(({ id, file }) => {
      updateInput({
        id,
        value: { isLoading: true } as Partial<IImageInput | IVideoInput>,
      });
      uploadMedia(file, id).then((cdnUrl) => {
        if (cdnUrl) sendUploadAnalytics();
      });
    });
  }, [
    isAuthorized,
    pendingUploads,
    clearPendingUploads,
    uploadMedia,
    updateInput,
  ]);

  // Handle pending Drive picker after auth
  useEffect(() => {
    if (!isAuthorized || !pendingDriveAction.current) return;

    const { id, inputType } = pendingDriveAction.current;
    // eslint-disable-next-line no-param-reassign
    pendingDriveAction.current = null;

    // Small delay to ensure auth state is fully propagated
    const timer = setTimeout(() => {
      openDrivePickerForInput(id, inputType);
    }, 100);

    return () => clearTimeout(timer);
    // pendingDriveAction is a stable ref, no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, openDrivePickerForInput]);
};

export default usePendingUploads;
